import React from 'react';
import { css } from '@emotion/css';
import { removeAuthCookie, DEFAULT_IB_AUTH_COOKIE_NAME } from 'web-lib-application-essentials/lib/cookie';
import { getAuthDomain } from 'web-lib-application-essentials/lib/urlUtil';

import { APIResponse } from '../../system-libraries/api-types';
import { deleteAccessToken } from '../../system-utils/api-utils/endpoints/delete-access-token';
import { MAX_DESKTOP_WIDTH_REM, mqBreakpoints } from '../../system-libraries/styles';
import { pxToRem } from '../../system-libraries/styles/utils';
import Config from '../../system-utils/config-manager';
import RedirectUtils from '../../system-utils/redirect-utils';
import { DeleteAccessTokenApiResponseBody } from '../../system-utils/api-utils/endpoints/types';
import { longTermToken } from '../../system-utils/events-manager';

type Props = {
  showLogout?: boolean;
};

export const HeaderNav: React.FC<Props> = ({ showLogout = true }) => {
  const onVMLogoClick: React.MouseEventHandler = e => {
    e.preventDefault();
    RedirectUtils.redirectToBiB();
  };

  const onAccountsClick: React.MouseEventHandler = e => {
    e.preventDefault();
    RedirectUtils.redirectToBiB();
  };

  const onLogoutClick: React.MouseEventHandler = e => {
    e.preventDefault();

    const deleteAccessTokenWithRedirect = async (): Promise<APIResponse<DeleteAccessTokenApiResponseBody>> => {
      try {
        const accessToken = longTermToken.getToken();
        await deleteAccessToken(accessToken);
        if (process.env.NODE_ENV === 'production') {
          removeAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME, getAuthDomain());
        }
        RedirectUtils.redirectToBiBLoginPage();
      } catch (error) {
        return {
          error,
        };
      }
    };

    deleteAccessTokenWithRedirect();
  };

  return (
    <div className={styles.header} data-testid='nba-headernav'>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <a onClick={onVMLogoClick} href={Config.get('bib_redirect_url')} data-testid='nba-headernav-vmlogo'>
            <img src={Config.get('image_cdn_base_url') + '/ui/shared-component-webapp/header/vmlogo2.svg'} className={styles.logoImageMobile} alt='To accounts' />
            <img src={Config.get('image_cdn_base_url') + '/ui/shared-component-webapp/header/vmlogo.svg'} className={styles.logoImage} alt='To accounts' />
          </a>
        </div>
        {showLogout && (
          <>
            <div className={styles.accountsButtonContainer}>
              <a className={styles.button} onClick={onAccountsClick} href={Config.get('login_and_registration.url')} data-testid='nba-headernav-accounts'>
                <img src={Config.get('image_cdn_base_url') + '/ui/shared-component-webapp/header/accounts.svg'} className={styles.accountButtonIcon} alt='To accounts' />
                <div className={styles.buttonText}>Accounts</div>
              </a>
            </div>
            <div>
              <a className={styles.button} onClick={onLogoutClick} href={Config.get('login_and_registration.url')} data-testid='nba-headernav-logout'>
                <img src={Config.get('image_cdn_base_url') + '/ui/shared-component-webapp/header/logout.svg'} className={styles.logoutButtonIcon} alt='Logout' />
                <div className={styles.buttonText}>Logout</div>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  header: css({
    backgroundColor: 'var(--color-primary)',
    flexShrink: 0,
    height: pxToRem(80),
    margin: '0 auto',
    width: '100%',
  }),
  container: css({
    alignItems: 'center',
    color: 'var(--color-white)',
    display: 'flex',
    flexDirection: 'row',
    fontSize: pxToRem(18),
    margin: '0 auto',
    paddingLeft: pxToRem(16),
    paddingRight: pxToRem(16),
    height: '100%',
    maxWidth: MAX_DESKTOP_WIDTH_REM,

    [mqBreakpoints.tablet]: {
      paddingLeft: pxToRem(48),
      paddingRight: pxToRem(48),
    },

    [mqBreakpoints.desktop]: {
      paddingLeft: pxToRem(80),
      paddingRight: pxToRem(80),
    },
  }),
  logoContainer: css({
    flexGrow: 1,
  }),
  logoImage: css({
    display: 'none',

    [mqBreakpoints.tablet]: {
      display: 'block',
      width: pxToRem(158),
      height: pxToRem(32),
    },

    [mqBreakpoints.desktop]: {
      width: pxToRem(215),
      height: pxToRem(46),
    },
  }),
  logoImageMobile: css({
    width: pxToRem(52),

    [mqBreakpoints.tablet]: {
      display: 'none',
    },
  }),
  accountsButtonContainer: css({
    marginRight: pxToRem(24),
  }),
  button: css({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    textDecoration: 'none',
  }),
  accountButtonIcon: css({
    width: pxToRem(24),
    height: pxToRem(24),

    [mqBreakpoints.tablet]: {
      width: pxToRem(28),
      height: pxToRem(28),
    },
  }),
  logoutButtonIcon: css({
    marginBottom: pxToRem(4),
    width: pxToRem(20),
    height: pxToRem(20),

    [mqBreakpoints.tablet]: {
      width: pxToRem(24),
      height: pxToRem(24),
    },
  }),
  buttonText: css({
    fontSize: pxToRem(14),
    fontFamily: 'var(--font-heading)',
    color: 'var(--color-white)',

    [mqBreakpoints.tablet]: {
      fontSize: pxToRem(18),
      lineHeight: pxToRem(24),
    },
  }),
};
